import { sum } from 'lodash';
import { useTranslation } from 'react-i18next';

import { EMPTY_STRING_FALLBACK } from '~/common/constants';
import { HomeScoreCategory } from '~/common/enums';
import { trpc } from '@/config/trpc';

import { FinanceIcon, HomeScoreCategoryIcon } from '@/shared/icons/Icons';
import MetricCard from '@/shared/components/2.0/layout/MetricCard';
import ProgressBar from '@/shared/components/2.0/ProgressBar';
import Widget from '../../HomeownerAssociation/Dashboard/Widget';

type CategoryColorsKey = keyof typeof COLORS.CATEGORY;

const COLORS = {
  CATEGORY: {
    effectiveness: {
      bg: '#d5e9fd',
      em: '#378add',
      text: '#378add',
    },
    happiness: {
      bg: '#dad8fe',
      em: '#6d28d9',
      text: '#4e48dc',
    },
    operationalManagement: {
      bg: '#cffaec',
      em: '#1b9f75',
      text: '#1b9f75',
    },
  },
  EMPHASIS_VALUE: '#da7a0c',
} as const;

const DECIMALS = 2;

const MAX_SCORE = 3;

const formatScore = (score: number | null | undefined) =>
  score?.toFixed(DECIMALS) ?? EMPTY_STRING_FALLBACK;

const getPercentage = (scoreInput: number | null | undefined) => {
  if (!scoreInput) return 0;

  return (scoreInput / MAX_SCORE) * 100;
};

type TotalScoreLineProps = {
  label: React.ReactNode;
  score: string;
  variant: CategoryColorsKey;
};

const TotalScoreLine: React.FC<TotalScoreLineProps> = ({ label, score, variant }) => {
  const colors = COLORS.CATEGORY[variant];

  return (
    <span
      className="align-items-center border-1 flex h-8 rounded-full border-solid px-3 text-sm font-medium"
      style={{
        backgroundColor: colors.bg,
        borderColor: colors.text,
        color: colors.text,
      }}
    >
      {label} {score}
    </span>
  );
};

const HomeScoreWidget: React.FC = () => {
  const { t } = useTranslation();
  const { data: currentScore } = trpc.homeScore.fullContractorData.useQuery();

  const categoryScores = {
    effectiveness: currentScore?.effectiveness ?? null,
    happiness: currentScore?.happiness ?? null,
    operationalManagement: currentScore?.operationalManagement ?? null,
  };

  const averageCategoryScore = (() => {
    const scores = Object.values(categoryScores).filter((score) => typeof score === 'number');

    if (!scores.length) return null;

    return sum(scores) / scores.length;
  })();

  const formattedScores = {
    average: formatScore(averageCategoryScore),
    effectiveness: formatScore(categoryScores.effectiveness),
    happiness: formatScore(categoryScores.happiness),
    max: formatScore(MAX_SCORE),
    operationalManagement: formatScore(categoryScores.operationalManagement),
  };

  const percentages = {
    effectiveness: getPercentage(categoryScores.effectiveness),
    happiness: getPercentage(categoryScores.happiness),
    operationalManagement: getPercentage(categoryScores.operationalManagement),
  };

  return (
    <div className="flex flex-wrap gap-4">
      <div className="my-2 grid flex-1 grid-cols-1 gap-3 md:grid-cols-[16.5rem_1fr] xl:grid-cols-[16.5rem_1fr_1fr_1fr]">
        <div className="relative shrink-0 space-y-4 rounded-2xl bg-white p-3">
          <div className="flex flex-col">
            <span className="text-xs font-semibold uppercase text-gray-400">
              {t('homeScore.totalScore')}
            </span>

            <div className="flex items-end gap-2">
              <span className="text-4xl font-bold text-gray-900">{formattedScores.average}</span>

              <span className="text-lg text-gray-400">/ {formattedScores.max}</span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <TotalScoreLine
              label={t('homeScore.dashboard.happiness')}
              score={formattedScores.happiness}
              variant="happiness"
            />
            <TotalScoreLine
              label={t('homeScore.dashboard.operational')}
              score={formattedScores.operationalManagement}
              variant="operationalManagement"
            />

            <TotalScoreLine
              label={t('homeScore.dashboard.effectiveness')}
              score={formattedScores.effectiveness}
              variant="effectiveness"
            />
          </div>
          <HomeScoreCategoryIcon
            category={
              currentScore?.category && currentScore.category !== 'Unspecified'
                ? HomeScoreCategory[currentScore.category]
                : HomeScoreCategory.NotApplicable
            }
            className="h-20 w-20"
          />
        </div>
        <Widget
          title={t('homeScore.dashboard.happiness')}
          iconBgColor={COLORS.CATEGORY.happiness.bg}
          icon={<FinanceIcon color={COLORS.CATEGORY.happiness.em} />}
          total={formattedScores.happiness}
          totalClass="font-extrabold text-2xl"
          totalStyle={{ color: COLORS.CATEGORY.happiness.em }}
        >
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <MetricCard
              label={t('homeScore.dashboard.nps')}
              value={currentScore?.nps}
              percentage={currentScore?.weights?.npsPercentage ?? 0}
              color={COLORS.CATEGORY.happiness.em}
              valueColor={COLORS.EMPHASIS_VALUE}
            />
            <MetricCard
              label={t('homeScore.dashboard.complaints')}
              value={currentScore?.complaints}
              percentage={currentScore?.weights?.complaintPercentage ?? 0}
              color={COLORS.CATEGORY.happiness.em}
              valueColor={COLORS.EMPHASIS_VALUE}
            />
          </div>
          <ProgressBar color={COLORS.CATEGORY.happiness.em} percentage={percentages.happiness} />
          <p className="text-right">
            Score: {formattedScores.happiness} / {formattedScores.max}
          </p>
        </Widget>
        <Widget
          title={t('homeScore.dashboard.operational')}
          iconBgColor={COLORS.CATEGORY.operationalManagement.bg}
          icon={<FinanceIcon color={COLORS.CATEGORY.operationalManagement.em} />}
          total={formattedScores.operationalManagement}
          totalClass="font-extrabold text-2xl"
          totalStyle={{ color: COLORS.CATEGORY.operationalManagement.em }}
        >
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <MetricCard
              label={t('homeScore.dashboard.reminders')}
              value={currentScore?.reminders}
              percentage={currentScore?.weights?.reminderPercentage ?? 0}
              color={COLORS.CATEGORY.operationalManagement.em}
            />
            <MetricCard
              label={t('homeScore.dashboard.cancellations')}
              value={currentScore?.cancellations}
              percentage={currentScore?.weights?.cancellationPercentage ?? 0}
              color={COLORS.CATEGORY.operationalManagement.em}
            />
            <MetricCard
              label={t('homeScore.dashboard.cooperation')}
              value={currentScore?.generalCooperation}
              percentage={currentScore?.weights?.cooperationPercentage ?? 0}
              color={COLORS.CATEGORY.operationalManagement.em}
            />
            <MetricCard
              label={t('homeScore.dashboard.cycleTimeAppointment')}
              value={currentScore?.cycleTimeAppointment}
              percentage={currentScore?.weights?.cycleTimeAppointment ?? 0}
              color={COLORS.CATEGORY.operationalManagement.em}
            />
            <MetricCard
              className="col-span-2"
              label={t('homeScore.dashboard.cycleTimeRepair')}
              value={currentScore?.cycleTimeRepair}
              percentage={currentScore?.weights?.cycleTimeRepair ?? 0}
              color={COLORS.CATEGORY.operationalManagement.em}
            />
          </div>
          <ProgressBar
            color={COLORS.CATEGORY.operationalManagement.em}
            percentage={percentages.operationalManagement}
          />
          <p className="text-right">
            Score: {formattedScores.operationalManagement} / {formattedScores.max}
          </p>
        </Widget>
        <Widget
          title={t('homeScore.dashboard.effectiveness')}
          iconBgColor={COLORS.CATEGORY.effectiveness.bg}
          icon={<FinanceIcon color={COLORS.CATEGORY.effectiveness.em} />}
          total={formattedScores.effectiveness}
          totalClass="font-extrabold text-2xl"
          totalStyle={{ color: COLORS.CATEGORY.effectiveness.em }}
        >
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <MetricCard
              label={t('homeScore.dashboard.averageCost')}
              value={currentScore?.averageCost}
              percentage={currentScore?.weights?.averageCostPercentage ?? 0}
              color={COLORS.CATEGORY.effectiveness.em}
            />
            <MetricCard
              label={t('homeScore.dashboard.multipleEstimates')}
              value={currentScore?.estimateAccuracy}
              percentage={currentScore?.weights?.multipleEstimatesPercentage ?? 0}
              color={COLORS.CATEGORY.effectiveness.em}
            />
            <MetricCard
              className="col-span-2"
              label={t('homeScore.dashboard.multipleInvoices')}
              value={currentScore?.invoiceAccuracy}
              percentage={currentScore?.weights?.multipleInvoicesPercentage ?? 0}
              color={COLORS.CATEGORY.effectiveness.em}
              valueColor={COLORS.EMPHASIS_VALUE}
            />
          </div>
          <ProgressBar
            color={COLORS.CATEGORY.effectiveness.em}
            percentage={percentages.effectiveness}
          />
          <p className="text-right">
            Score: {formattedScores.effectiveness} / {formattedScores.max}
          </p>
        </Widget>
      </div>
    </div>
  );
};

export default HomeScoreWidget;
